import { colors } from '@meterup/atto';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { z } from 'zod';

import { useLocalStorage } from '../hooks/useLocalStorage';

/** Runtime validation for `localStorage` (matches bootstrap script + `useStorage` shape). */
export const themePreferenceSchema = z.enum(['dark', 'light', 'system']);

export type Theme = z.infer<typeof themePreferenceSchema>;

function getMetaById(id: string): HTMLMetaElement | null {
  const el = document.getElementById(id);
  return el instanceof HTMLMetaElement ? el : null;
}

function queryMeta(selector: string): HTMLMetaElement | null {
  const el = document.querySelector(selector);
  return el instanceof HTMLMetaElement ? el : null;
}

function getOrCreateMeta(id: string, name: string): HTMLMetaElement {
  const existing = getMetaById(id);
  if (existing) return existing;
  const el = document.createElement('meta');
  el.setAttribute('name', name);
  el.id = id;
  document.head.appendChild(el);
  return el;
}

/** Updates body classes, `color-scheme` on `<html>`, and theme meta tags to match resolved mode. */
function applyMeterThemeToDocument(dark: boolean): void {
  document.body.classList.remove('m-dark', 'm-light');
  document.body.classList.add(dark ? 'm-dark' : 'm-light');
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';

  const colorSchemeMeta =
    getMetaById('meter-color-scheme') ??
    queryMeta('meta[name="color-scheme"]') ??
    getOrCreateMeta('meter-color-scheme', 'color-scheme');
  colorSchemeMeta.setAttribute('content', dark ? 'dark' : 'light');

  const themeColorMeta =
    getMetaById('meter-theme-color') ?? getOrCreateMeta('meter-theme-color', 'theme-color');
  const themeBg = dark ? colors.bgApplicationDark : colors.bgApplicationLight;
  themeColorMeta.setAttribute('content', `${themeBg}`);
}

export interface ThemeData {
  theme: Theme;
  setTheme: (val: Theme) => void;
  dark: boolean;
}

/** Fallback before `ThemeProvider` mounts; avoid using context outside the provider. */
const defaultThemeData: ThemeData = {
  theme: 'system',
  setTheme: () => {},
  dark: false,
};

export const ThemeContext = createContext<ThemeData>(defaultThemeData);

export default function ThemeProvider({ children }: { children?: ReactNode }) {
  const [theme, setTheme] = useLocalStorage<Theme>('theme', 'system', {
    schema: themePreferenceSchema,
  });
  const [dark, setDark] = useState(() =>
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : theme === 'dark',
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') setDark(e.matches);
    };
    mq.addEventListener('change', onChange);
    if (theme === 'system') {
      setDark(mq.matches);
    } else {
      setDark(theme === 'dark');
    }
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  useLayoutEffect(() => {
    applyMeterThemeToDocument(dark);
  }, [dark]);

  const providerValue = useMemo(
    (): ThemeData => ({ theme, setTheme, dark }),
    [theme, setTheme, dark],
  );
  return <ThemeContext.Provider value={providerValue}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeData {
  return useContext(ThemeContext);
}
